import { useState } from "react";
import {
  CalendarDays,
  Home,
  KanbanSquare,
  List,
  Plus,
} from "lucide-react";
import { TarefasHome } from "./TarefasHome";
import { TarefasLista } from "./TarefasLista";
import { TarefasKanban } from "./TarefasKanban";
import { TarefasCalendario } from "./TarefasCalendario";
import { TarefaFormDialog } from "./TarefaFormDialog";
import { useAuth } from "@/hooks/useAuth";
import type { Tarefa, TarefasView } from "@/types/tarefas";

const VIEWS: { id: TarefasView; label: string; icon: typeof Home }[] = [
  { id: "home", label: "Home", icon: Home },
  { id: "lista", label: "Lista", icon: List },
  { id: "kanban", label: "Kanban", icon: KanbanSquare },
  { id: "calendario", label: "Calendário", icon: CalendarDays },
];

const TITULOS: Record<TarefasView, { title: string; subtitle: string }> = {
  home: {
    title: "Tarefas",
    subtitle: "Organize o dia a dia: ligações, lembretes e entregas do time.",
  },
  lista: {
    title: "Lista",
    subtitle: "Filtre por status, responsável, prazo e cliente.",
  },
  kanban: {
    title: "Kanban",
    subtitle: "Arraste os cards para atualizar o status da tarefa.",
  },
  calendario: {
    title: "Calendário",
    subtitle: "Programe compromissos e veja o que vence em cada dia.",
  },
};

export function TarefasModule() {
  const { isVendedorScoped } = useAuth();
  const canVerTodas = !isVendedorScoped();

  const [view, setView] = useState<TarefasView>("home");
  const [apenasMinhas, setApenasMinhas] = useState(!canVerTodas);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Tarefa | null>(null);
  const [defaultPrazo, setDefaultPrazo] = useState<string | null>(null);

  const openNova = (prazo?: string | null) => {
    setEditing(null);
    setDefaultPrazo(prazo ?? null);
    setFormOpen(true);
  };

  const openEdit = (tarefa: Tarefa) => {
    setEditing(tarefa);
    setDefaultPrazo(null);
    setFormOpen(true);
  };

  const { title, subtitle } = TITULOS[view];

  return (
    <div className="crm-fynix -m-6 min-h-[calc(100vh-5.5rem)] p-6">
      <header className="flex flex-col xl:flex-row xl:items-center gap-4 mb-5">
        <div className="min-w-[220px]">
          <h1 className="crm-title text-2xl text-fynix-ink">{title}</h1>
          <p className="text-sm text-fynix-muted">{subtitle}</p>
        </div>

        <div className="flex-1" />

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {canVerTodas && view !== "home" && (
            <label className="flex items-center gap-2 text-sm text-fynix-ink-soft h-11 px-3 rounded-2xl border border-fynix-line bg-white">
              <input
                type="checkbox"
                checked={apenasMinhas}
                onChange={(e) => setApenasMinhas(e.target.checked)}
                className="rounded border-fynix-line"
              />
              Minhas tarefas
            </label>
          )}
          <button
            type="button"
            onClick={() => openNova()}
            className="h-11 px-4 text-sm flex items-center gap-2 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
          >
            <Plus className="h-4 w-4" /> Nova tarefa
          </button>
        </div>
      </header>

      <nav className="flex flex-wrap gap-2 mb-5">
        {VIEWS.map(({ id, label, icon: Icon }) => {
          const active = view === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setView(id)}
              className={`h-10 px-4 rounded-2xl text-sm font-medium flex items-center gap-2 transition-colors ${
                active
                  ? "bg-fynix-brand text-white"
                  : "bg-white border border-fynix-line text-fynix-ink-soft hover:bg-fynix-brand-soft"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </nav>

      {view === "home" && (
        <TarefasHome
          onNavigate={setView}
          onOpenTarefa={openEdit}
        />
      )}
      {view === "lista" && (
        <TarefasLista
          apenasMinhas={apenasMinhas}
          onOpenTarefa={openEdit}
        />
      )}
      {view === "kanban" && (
        <TarefasKanban
          apenasMinhas={apenasMinhas}
          onOpenTarefa={openEdit}
        />
      )}
      {view === "calendario" && (
        <TarefasCalendario
          apenasMinhas={apenasMinhas}
          onOpenTarefa={openEdit}
          onNovaNoDia={(iso) => openNova(iso)}
        />
      )}

      <TarefaFormDialog
        open={formOpen}
        onOpenChange={(o) => {
          setFormOpen(o);
          if (!o) {
            setEditing(null);
            setDefaultPrazo(null);
          }
        }}
        tarefa={editing}
        defaultPrazo={defaultPrazo}
      />
    </div>
  );
}
